import axios from 'axios';
import { t } from '../../utils/i18n';
import { uploadPhoto } from '../../services/uploadPhotoServices';
import { SCREEN } from '../../utils/constants';
import { fileUploadChannel, form, primaryColor } from '../../utils/variables';
import { toastError } from '../../views/components/error';
import { navigate } from './movePages';

let continueHandler = null;

/**
 * The card list comes from channelable.config (a JSON string set per project).
 * A channel saved without config used to render a header with ZERO upload
 * targets (the generic dropzone in the markup is permanently hidden and has
 * no handlers) — fall back to a single generic card so the screen is always
 * actionable.
 */
function getUploadConfigOptions() {
    try {
        const parsed = JSON.parse(fileUploadChannel.channelable?.config);
        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
        }
    } catch (e) {
        // null / malformed config — use the fallback card
    }
    return [{ id: 1, name: t('upload_your_photos') }];
}

export function uploadPhotosListeners(onContinue) {
    if (form?.form?.custom_fields?.length > 0) {
        document
            .querySelector('.uploadPhotos__container .back-arrow')
            ?.classList.add('hidden');
        document
            .querySelector(
                '.uploadPhotos__container .uploadPhotos__actions .backOneStep__btn'
            )
            ?.classList.add('hidden');
    }
    const btnContinueUploads = document.querySelector(
        '.uploadPhotos__container .uploadPhotosContinue__btn'
    );
    if (fileUploadChannel.channelable.required) {
        btnContinueUploads.innerHTML = `${t('continue')} <i class="ri-arrow-right-line"></i>`;
        btnContinueUploads.classList.add('disabled');
        feather.replace();
    }
    document.querySelector(
        '.uploadPhotos__container .uploadPhotosContent'
    ).style.background = `${primaryColor}09`;

    document.querySelector(
        '.uploadPhotos__container .uploadPhotosTop__heading-desc'
    ).innerText = fileUploadChannel.channelable.description;

    uploadPhotosListen(onContinue);
}

function uploadPhotosListen(onContinue) {
    loadListConfigOption();

    loadEventForUploadFiles();

    /* On click enter */
    const screenPhone = document.querySelector('.screen--uploads');

    if (screenPhone) {
        screenPhone.addEventListener('keydown', (event) => {
            if (
                event.key === 'Enter' &&
                !document
                    .querySelector('.uploadPhotosContinue__btn')
                    .classList.contains('disabled')
            ) {
                document.querySelector('.uploadPhotosContinue__btn').click();
            }
        });
    }

    /* on click Continue*/
    const continueBtn = document.querySelector('.uploadPhotosContinue__btn');
    if (continueHandler) {
        continueBtn.removeEventListener('click', continueHandler);
    }
    continueHandler = () => {
        if (typeof onContinue === 'function') {
            onContinue();
        } else {
            navigate(SCREEN.THE_END);
        }
    };
    continueBtn.addEventListener('click', continueHandler);
}

function validateImage(image) {
    const extensions = ['jpeg', 'png', 'jpg', 'gif'];
    const fileExtension = image.type.split('/').pop();
    const fileSize = Math.round(image.size / 1024);

    if (fileSize > 100000) {
        return {
            checkImage: false,
            error: t('image_size_error'),
        };
    }

    if (!extensions.includes(fileExtension)) {
        return {
            checkImage: false,
            error: t('image_type_error'),
        };
    }

    return {
        checkImage: true,
    };
}

function handleUploadFiles(files, indexFileOptionConfig) {
    const photosUploadButton = document.querySelector(
        '.uploadPhotos__container .uploadPhotosContinue__btn'
    );
    let countFile = 0;
    Array.from(files)?.length > 0 &&
        Array.from(files)?.forEach((fileImage) => {
            const validImage = validateImage(fileImage);
            if (validImage?.checkImage) {
                const CancelToken = axios.CancelToken;
                const source = CancelToken.source();
                const elementItem = document.querySelector(
                    `.imagesUploadItem-${indexFileOptionConfig}`
                );
                compressImage(fileImage, (compressedItem) => {
                    const formData = new FormData();
                    formData.append('file', compressedItem);
                    formData.append('original_name', fileImage.name);
                    formData.append('name', fileImage.name);
                    if(photosUploadButton) {
                        photosUploadButton.classList.add('disabled');
                    }
                    uploadPhoto(formData, elementItem, source)
                        .then(() => {
                            countFile += 1;

                            if (countFile == Array.from(files)?.length) {
                                elementItem
                                    .querySelector('.button')
                                    ?.classList.add('upload-success');
                                elementItem.querySelector(
                                    '.button'
                                ).innerHTML = ` <svg
                                                data-src="assets/icons/check-icon.svg"></svg>
                                            <span>${t('uploaded')}</span>`;

                                checkRequiredUploads();
                            }
                            photosUploadButton.classList.remove('disabled');
                        })
                        .catch(() => {
                            elementItem
                                .querySelector('.button')
                                ?.classList.add('upload-failed');
                            elementItem.querySelector('.button').innerHTML = `  
                                            <i data-feather="x-circle"></i>
                                            <span>${t('upload_failed_status')}</span>`;
                            feather.replace();
                            if(photosUploadButton) {
                                photosUploadButton.classList.remove('disabled');
                            }
                        });
                });
            } else {
                toastError(validImage?.error);
            }
        });
}

export function compressImage(imageFile, callback) {
    let options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
    };
    imageCompression(imageFile, options)
        .then(function (compressedFile) {
            callback(compressedFile);
        })
        .catch(function (error) {
            toastError(error.message);
        });
}

function handleFileInputChange(event) {
    event.preventDefault();
    const files = event.target.files;
    handleUploadFiles(files, event.target.getAttribute('data-id'));
}

function handleFileDragOver(event) {
    event.preventDefault();
    this.classList.add('hover');
}

function handleFileDragLeave(event) {
    event.preventDefault();
    this.classList.remove('hover');
}

function handleFileDrop(event) {
    const inputFile = document.querySelector(
        '.uploadPhotos__container input[type=file]'
    );
    inputFile.value = '';
    event.preventDefault();
    this.classList.remove('hover');
    const files = event.dataTransfer.files;
    handleUploadFiles(files, event.target.getAttribute('data-id'));
}

function handleClickInputFile() {
    this.value = '';
}

function checkRequiredUploads() {
    const photosUploadSuccess = document.querySelectorAll(
        '.uploadPhotos__container .imagesUpload .imagesUploadItem .button'
    );
    if (!fileUploadChannel.channelable.required) {
        document.querySelector(
            '.uploadPhotos__container .uploadPhotosContinue__btn'
        ).innerHTML = `${
            photosUploadSuccess.length > 0 ? t('continue') : t('skip_step')
        }
            <i class="ri-arrow-right-line"></i>`;
        feather.replace();
    } else {
        let countOptionFileConfig = 0;

        photosUploadSuccess.forEach((photoItem) => {
            if (photoItem.classList.contains('upload-success')) {
                countOptionFileConfig += 1;
            }

            if (
                countOptionFileConfig ==
                getUploadConfigOptions().length
            ) {
                document
                    .querySelector(
                        '.uploadPhotos__container .uploadPhotosContinue__btn'
                    )
                    .classList.remove('disabled');
            }
        });
    }
}

function loadListConfigOption() {
    const imagesUpload = document.querySelector(
        '.uploadPhotos__container .imagesUpload'
    );
    if (imagesUpload) {
        // The listener can run more than once per session (e.g. re-entering
        // the flow) — re-appending would duplicate cards and lose upload state.
        if (imagesUpload.children.length === 0) {
            let optionList = ``;
            for (const i of getUploadConfigOptions()) {
                optionList = optionList.concat(`
                    <label class="label-${i.id}" data-id="${i.id}">
                        <div class="imagesUploadItem imagesUploadItem-${i.id}" data-id="${i.id}">
                            <div class="imagesUploadItem__info imagesUploadItem__info-${i.id}" data-id="${i.id}">
                                <div class="wrapName wrapName-${i.id}" data-id="${i.id}">
                                    <span class="imagesName imagesName-${i.id}" data-id="${i.id}">
                                    ${i.name}
                                    </span>
                                </div>
                            </div>
                            <div class="button button-${i.id}" data-id="${i.id}">
                            
                                    <span class="text-${i.id}" data-id="${i.id}">${t('upload')}</span>
                                    <input type="file" class="file-${i.id}" data-id="${i.id}" multiple hidden accept="image/png, image/gif, image/jpeg" />
                            </div>
                        </div>
                    </label>
                `);
            }

            imagesUpload.insertAdjacentHTML(`beforeend`, optionList);
        }
    }
}

function loadEventForUploadFiles() {
    for (const i of getUploadConfigOptions()) {
            const inputFile = document.querySelector(
                `.button-${i.id} input[type=file]`
            );
            const labelDrag = document.querySelector(
                `.label-${i.id}`
            );
            if (inputFile) {
                inputFile.addEventListener('change', handleFileInputChange);
                inputFile.addEventListener('click', handleClickInputFile);
            }

            if (labelDrag) {
                labelDrag.addEventListener('dragover', handleFileDragOver);
                labelDrag.addEventListener('dragleave', handleFileDragLeave);
                labelDrag.addEventListener('drop', handleFileDrop);
            }
        }
}
